import type { DocumentationRequestDocumentType } from '#modules/DocumentUpload/DocumentUpload'
import type { EskatCompletedEvent } from '#modules/Eskat/Eskat.types'
import type { History } from 'history'
import type { Dispatch } from 'redux'

import {
	featureFlagClient,
	Region,
	RegionProvider,
	trackingService,
} from '@monthio/react-components'
import {
	setContext,
	setTag,
	setTags,
	setUser,
	withSentryRouting,
} from '@sentry/react'
import { giveConsent } from '#api/consentApi'
import { eskatApiClient } from '#api/eskatApiClient'
import { pensionApiClient } from '#api/pensionApi'
import config from '#config'
import { AssetExpensesModuleProvider } from '#modules/AssetExpenses'
import { AssetOverviewModuleProvider } from '#modules/AssetOverview'
import { AssetReplacementModuleProvider } from '#modules/AssetReplacement'
import { BankModuleProvider } from '#modules/Bank'
import { BudgetModuleProvider } from '#modules/Budget'
import { BudgetConsumerEmailModuleProvider } from '#modules/BudgetConsumerEmail'
import { BudgetCreatorModuleProvider } from '#modules/BudgetCreator'
import { ClientConsentModuleProvider } from '#modules/ClientConsent'
import { CustomQuestionnaireModuleProvider } from '#modules/CustomQuestionnaire'
import { DebtOverviewModuleProvider } from '#modules/DebtOverview'
import { DocumentUploadModuleProvider } from '#modules/DocumentUpload'
import { EskatModuleProvider } from '#modules/Eskat'
import { FlowCompletedModuleProvider } from '#modules/FlowCompleted'
import { FlowDeletedModuleProvider } from '#modules/FlowDeleted'
import { IncomeVerificationModuleProvider } from '#modules/IncomeVerification'
import { LandingPageModuleProvider } from '#modules/LandingPage'
import { PensionModuleProvider } from '#modules/Pension'
import { QuestionnaireModuleProvider } from '#modules/Questionnaire'
import { SamsungBrowserWarningModuleProvider } from '#modules/SamsungBrowserWarning'
import { SavingsCapacityModuleProvider } from '#modules/SavingsCapacity'
import { lazy, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Route, Switch, useHistory } from 'react-router-dom'
import styled from 'styled-components'

import type { HistoryWithEntries } from './routeOrchestrator'
import type { RootState } from './store/reducers'
import type { AuthenticationState } from './store/reducers/authenticateReducer'
import type {
	AuthenticateSmartcheckSessionDto,
	SmartCheckConfigurationDto,
} from './types/authenticateSmartcheckSessionDto'

import { budgetApiClient } from './api/budgetApiClient'
import { AppContentWithAnimation } from './AppContentWithAnimation'
import Header from './components/SmartcheckFlowHeader/Header'
import {
	DEFAULT_NS,
	PrimaryNamespaceTranslations,
} from './components/PrimaryNamespaceTranslations/PrimaryNamespaceTranslations'
import getQueryStringParameters from './getQueryStringParameters'
import { GenericLandingPage } from './pages/GenericLandingPage'
import { getFlowSteps } from './pages/LandingPage/flowStepUtils'
import { ParringIncompleteScreen } from './pages/ParringIncompleteScreen'
import { flowOrchestrationService } from './services/flowOrchestrationService/flowOrchestrationService'
import { loadingContainerService } from './services/loadingContainerService'
import { redirectService } from './services/redirectService'
import {
	goToFlow as reduxGoToFlow,
	goToWhitelabelSubdomainLanding,
} from './store/actions/flowEntryStatemachineActions'
import { AuthenticateStatus } from './store/reducers/authenticateReducer'
import { FlowEntryStatemachineStatus } from './store/reducers/flowEntryStatemachineReducer'
import { ModuleDefinition } from './types/authenticateSmartcheckSessionDto'
import { UnauthorizedPage } from './UnauthorizedPage'
import { userConsentManager } from './userConsentManager'
import { applyCustomColors } from './utils/customColors'

const noop = async () => {}
const startNoop = async () => ({})

const LazyCustomQuestionnaire = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/CustomQuestionnaire').then((m) => ({
		default: m.CustomQuestionnaire,
	})),
)

const LazyDocumentUpload = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/DocumentUpload').then((m) => ({
		default: m.DocumentUpload,
	})),
)

const LazyPension = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/Pension').then((m) => ({
		default: m.Pension,
	})),
)

const LazyEskat = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/Eskat').then((m) => ({
		default: m.Eskat,
	})),
)

const LazyAssetExpenses = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/AssetExpenses').then((m) => ({
		default: m.AssetExpenses,
	})),
)

const LazyAssetOverview = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/AssetOverview').then((m) => ({
		default: m.AssetOverview,
	})),
)

const LazyAssetReplacement = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/AssetReplacement').then((m) => ({
		default: m.AssetReplacement,
	})),
)

const LazyBank = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/Bank').then((m) => ({
		default: m.Bank,
	})),
)

const LazyBudget = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/Budget').then((m) => ({
		default: m.Budget,
	})),
)

const LazyDebtOverview = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/DebtOverview').then((m) => ({
		default: m.DebtOverview,
	})),
)

const LazyQuestionnaire = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/Questionnaire').then((m) => ({
		default: m.Questionnaire,
	})),
)

const LazyLandingPage = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('./Modules/LandingPage').then((m) => ({
		default: m.LandingPage,
	})),
)

const LazyIncomeVerification = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/IncomeVerification').then((m) => ({
		default: m.IncomeVerification,
	})),
)

const LazySavingsCapacity = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/SavingsCapacity').then((m) => ({
		default: m.SavingsCapacity,
	})),
)

const LazyBudgetCreator = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/BudgetCreator').then((m) => ({
		default: m.BudgetCreator,
	})),
)

const LazyBudgetConsumerEmail = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/BudgetConsumerEmail').then((m) => ({
		default: m.BudgetConsumerEmail,
	})),
)

const LazyClientConsent = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/ClientConsent').then((m) => ({
		default: m.ClientConsent,
	})),
)
const LazyFlowCompleted = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/FlowCompleted').then((m) => ({
		default: m.FlowCompleted,
	})),
)
const LazyFlowDeleted = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/FlowDeleted').then((m) => ({
		default: m.FlowDeleted,
	})),
)
const LazySamsungBrowserWarning = lazy(() =>
	// @ts-expect-error TS cannot follow the type here
	import('#modules/SamsungBrowserWarning').then((m) => ({
		default: m.SamsungBrowserWarning,
	})),
)

interface RoutesModuleProps {
	history: History<any>
	applicantId: string | null
	smartCheckSession?: AuthenticateSmartcheckSessionDto
	smartCheckConfiguration?: SmartCheckConfigurationDto
	authenticateState: AuthenticationState
	flowEntryStatus: FlowEntryStatemachineStatus
	goToFlow: (applicantId: string) => void
	goToWhitelabelSubdomainLanding: () => void
}

const PageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;

	overflow: hidden;
`

const SentryRoute = withSentryRouting(Route)

export interface AppRoute {
	name: string
	routes: { route: string; name: string; getComponent: () => JSX.Element }[]
}

const Routes = (props: RoutesModuleProps) => {
	const [consumerInputsStepIndex, setConsumerInputsStepIndex] = useState<
		number | undefined
	>(undefined)
	const history: HistoryWithEntries = useHistory<any>()
	const {
		smartCheckSession,
		smartCheckConfiguration,
		authenticateState,
		goToFlow,
	} = props

	const queryStringParams = getQueryStringParameters(document.location.search)
	const applicantId: string =
		queryStringParams.sessionid ?? queryStringParams.caseid
	const parringId: string = queryStringParams.parringid

	const authenticationInProgress =
		authenticateState.status === AuthenticateStatus.AUTHENTICATING ||
		authenticateState.status ===
			AuthenticateStatus.AUTHENTICATING_WITH_SUBDOMAIN
	const authenticationFailed =
		authenticateState.status === AuthenticateStatus.AUTHENTICATE_FAILURE
	const authenticationSucceeded =
		authenticateState.status === AuthenticateStatus.AUTHENTICATE_SUCCESS

	flowOrchestrationService.setHistory(history)

	useEffect(() => {
		if (applicantId) {
			setTag('ApplicantId', applicantId)
			setUser({ id: applicantId })
			goToFlow(applicantId)
		} else if (parringId) {
			setTag('ApplicantId', parringId)
			setUser({ id: parringId })
			goToFlow(parringId)
		} else {
			props.goToWhitelabelSubdomainLanding()
			setTag('WhitelabelSubdomainLanding', true)
			loadingContainerService.hide()
			console.log(
				'loadingContainerService.hide(): moduleCompleted',
				'WhitelabelSubdomainLanding',
			)
		}

		const subscription = featureFlagClient.subscribe(() => {
			const launchDarkly = featureFlagClient.getAllFlags()
			const mappedFlags = Object.keys(launchDarkly).reduce(
				(accumulator, currentValue) => {
					accumulator[currentValue] = launchDarkly[currentValue].value
					return accumulator
				},
				{} as Record<string, boolean>,
			)
			setContext('feature-flags', mappedFlags)
			trackingService.trackTechnicalEvent('feature-flags-evaluated', {
				flags: mappedFlags,
			})
		})

		return () => {
			subscription()
		}
	}, [])

	useEffect(() => {
		if (!smartCheckConfiguration?.shouldUseCustomColorsInFlow) {
			setTag('customColors', false)
			return
		}

		const customColors = smartCheckConfiguration?.customColors
		setTag('customColors', true)

		applyCustomColors({
			primaryColor: customColors?.primaryColor,
			secondaryColor: customColors?.secondaryColor,
		})
	}, [
		smartCheckConfiguration?.customColors,
		smartCheckConfiguration?.shouldUseCustomColorsInFlow,
	])

	useEffect(() => {
		if (props.flowEntryStatus !== FlowEntryStatemachineStatus.IN_FLOW) {
			return
		}

		if (authenticationSucceeded) {
			if (smartCheckSession?.client.id) {
				setTags({
					ApplicantSessionId: smartCheckSession?.applicantSessionId,
					TenantId: smartCheckSession?.client.id,
					RegionId: smartCheckSession?.regionId,
					CaseConfiguration: smartCheckSession?.smartCheckConfiguration.id,
				})
				userConsentManager.setApplicantData(
					applicantId,
					smartCheckSession?.applicantSessionId,
				)

				// Set region id for intercom
				featureFlagClient.initialize(config, {
					clientId: smartCheckSession.client.id,
				})
			}
		}
	}, [authenticationSucceeded])

	const { ready: translationsReady } = useTranslation()

	if (
		props.flowEntryStatus !==
			FlowEntryStatemachineStatus.IN_WHITELABEL_SUBDOMAIN &&
		(authenticationInProgress || !translationsReady)
	) {
		loadingContainerService.show()
		console.log(
			'loadingContainerService.show(): moduleCompleted',
			props.flowEntryStatus,
		)
		return null
	}
	loadingContainerService.hide()

	if (
		props.flowEntryStatus ===
		FlowEntryStatemachineStatus.IN_WHITELABEL_SUBDOMAIN
	) {
		// Fix region dependency or merge supported languages in AllLanguagesProvider
		return (
			<RegionProvider regionId={Region.Denmark}>
				<GenericLandingPage consumerInputsStepIndex={consumerInputsStepIndex} />
			</RegionProvider>
		)
	}

	if (authenticationFailed) {
		return (
			<UnauthorizedPage
				errorCode={authenticateState.authenticateError?.errorCode}
			/>
		)
	}

	const propertiesToForwardToTransactionClientApp = {
		sessionId: props.applicantId,
		hasExplicitConsent: props.smartCheckConfiguration?.hasExplicitConsent,
		clientName:
			props.smartCheckConfiguration?.legalBrandName ??
			props.smartCheckSession?.client.name ??
			'',
		calculationReferenceDate:
			props.smartCheckSession?.case.calculationReferenceDate,
		fetchOnlyAccounts:
			props.smartCheckConfiguration?.moduleConfiguration.moduleIds.includes(
				ModuleDefinition.AccountVerification,
			),
	}

	const regionId = authenticateState.smartCheckAuthenticationDto?.regionId
	const primaryTranslationNamespace =
		smartCheckSession?.smartCheckConfiguration.primaryTranslationNamespace ??
		DEFAULT_NS

	return (
		<RegionProvider regionId={regionId}>
			<PrimaryNamespaceTranslations namespace={primaryTranslationNamespace}>
				<PageWrapper>
					<Header
						smartCheckSession={smartCheckSession}
						smartCheckConfiguration={smartCheckConfiguration}
						consumerInputsStepIndex={consumerInputsStepIndex}
					/>
					<AppContentWithAnimation>
						<Switch>
							<SentryRoute path={config.routeCodes.flowSplash} exact>
								<LandingPageModuleProvider
									moduleType={ModuleDefinition.LandingPage}
									startCall={async () => ({
										flowSteps: (await getFlowSteps(smartCheckSession!)) as any,
									})}
									finishCall={noop}
									config={{
										clientName:
											smartCheckSession?.smartCheckConfiguration
												.legalBrandName ??
											smartCheckSession?.client.name ??
											'',
										logo: smartCheckSession?.smartCheckConfiguration.logo,
									}}
								>
									<LazyLandingPage />
								</LandingPageModuleProvider>
							</SentryRoute>
							<SentryRoute
								path={config.routeCodes.clientConsent}
								exact
								render={() => (
									<ClientConsentModuleProvider
										moduleType={ModuleDefinition.ClientConsent}
										startCall={async () => ({
											consents:
												smartCheckSession!.smartCheckConfiguration.consents,
										})}
										finishCall={giveConsent}
										config={{
											clientName:
												smartCheckSession!.smartCheckConfiguration
													.legalBrandName ??
												smartCheckSession?.client.name ??
												'',
										}}
									>
										<LazyClientConsent />
									</ClientConsentModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.eskat}
								exact
								render={() => (
									<EskatModuleProvider
										moduleType={ModuleDefinition.Eskat}
										startCall={async () => {
											eskatApiClient.initialize()
											const session =
												await eskatApiClient.registerEskatSession()
											const applicant = await budgetApiClient.getApplicant()
											const applicantSession =
												await budgetApiClient.getApplicantSession(
													session.sessionId,
												)

											return {
												caseId: applicant.caseId,
												applicantId: applicant.id,
												applicantSessionId: applicantSession.id,
												applicantSessionQuestionnaireHashedCpr:
													applicantSession.consumerInputJson
														? JSON.parse(applicantSession.consumerInputJson)
																?.personalInformation?.hashedCprNumber
														: null,
												applicantClientInputHashedCpr:
													applicant.hashedConsumerCpr ?? null,
												clientId: applicant.clientId,
												isFinished: session.finishedOn != null,
											}
										}}
										finishCall={noop}
										config={{
											environment: config.environment,
											getConsent: async () => {
												return await eskatApiClient.getConsent()
											},
											fetchAndPersistEskatData: async () => {
												await eskatApiClient.fetchAndPersistEskatData()
											},
											getApplicantsByCaseId: async (caseId: string) => {
												return await budgetApiClient.getApplicantsByCaseId(
													caseId,
												)
											},
											postTestData: async (demoProfile: string | null) => {
												await eskatApiClient.postTestData(demoProfile)
											},
											openPopup: (
												eskatFormRef: React.RefObject<HTMLFormElement>,
											) => {
												try {
													const eskatWindow = window.open('', 'eskat-iframe')
													eskatWindow?.focus()
													eskatFormRef.current?.submit()
												} catch (error) {
													/* No need to handle popup is blocked */
												}
											},
											registerEskatMessageListener: (
												handler: (event: EskatCompletedEvent) => Promise<void>,
											) => {
												const listener = (
													event: MessageEvent<EskatCompletedEvent>,
												) => {
													if (event.data?.eventId === 'monthio-eskat-consent') {
														handler(event?.data)
													}
												}

												window.addEventListener('message', listener, true)

												return () =>
													window.removeEventListener('message', listener, true)
											},
										}}
									>
										<LazyEskat />
									</EskatModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.pension}
								exact
								render={() => (
									<PensionModuleProvider
										moduleType={ModuleDefinition.Pension}
										startCall={async () => {
											pensionApiClient.initialize()
											const authenticateDto =
												await pensionApiClient.authenticateSession(
													props.smartCheckConfiguration?.pensionsInfoMinAge,
												)
											return {
												pensionSessionId: authenticateDto.pensionSessionId,
												shouldSkip:
													authenticateDto.isApplicantEligibleForPensionsInfo ==
														false || authenticateDto.finishedOn != null,
											}
										}}
										finishCall={noop}
										config={{
											pensionWindowUrl: config.pensionInfoWindowUrl,
											getMitIdLoginUrl: async () => {
												return await pensionApiClient.getMitIdLoginUrl()
											},
											openPopup: (
												formRef: React.RefObject<HTMLFormElement>,
											) => {
												try {
													const pensionWindow = window.open('', 'pinfoForm')
													pensionWindow?.focus()
													formRef.current?.submit()
												} catch (error) {
													// No need to handle popup is blocked
												}
											},
											registerPensionMessageListener: (
												updateVisibilityHandler: (event: any) => void,
												receivePopUpMessageHandler: (event: any) => void,
											) => {
												window.addEventListener(
													'visibilitychange',
													updateVisibilityHandler,
												)
												window.addEventListener(
													'message',
													receivePopUpMessageHandler,
													false,
												)

												return () => {
													window.removeEventListener(
														'message',
														receivePopUpMessageHandler,
														true,
													)
													window.removeEventListener(
														'visibilitychange',
														updateVisibilityHandler,
														true,
													)
												}
											},
										}}
									>
										<LazyPension />
									</PensionModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.bankSelectionPage}
								render={() => (
									<BankModuleProvider
										moduleType={ModuleDefinition.Bank}
										startCall={startNoop}
										finishCall={noop}
										config={{
											isProduction: config.isProduction,
											forwardedProperties:
												propertiesToForwardToTransactionClientApp as any,
											clientName:
												smartCheckSession?.smartCheckConfiguration
													.legalBrandName ??
												smartCheckSession?.client.name ??
												'',
										}}
									>
										<LazyBank />
									</BankModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.incomeVerification}
								exact
								render={() => (
									<IncomeVerificationModuleProvider
										moduleType={ModuleDefinition.IncomeVerification}
										startCall={budgetApiClient.getIncomeVerification}
										finishCall={budgetApiClient.finishIncomeVerification}
										config={{
											applicantProvidedDocumentConfigurations:
												smartCheckSession?.applicantProvidedDocumentConfigurations,
											shouldAllowFileUpload:
												smartCheckSession!.smartCheckConfiguration
													.shouldAllowFileUpload,
											shouldShowConsumerNote:
												smartCheckSession!.smartCheckConfiguration
													.shouldShowConsumerNote,
										}}
									>
										<LazyIncomeVerification />
									</IncomeVerificationModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.consumerInputs}
								exact
								render={() => (
									<QuestionnaireModuleProvider
										moduleType={ModuleDefinition.Questionnaire}
										startCall={async () => {
											return {}
										}}
										finishCall={budgetApiClient.finishQuestonnaireModule}
										config={{
											validateCprAgainstServer:
												budgetApiClient.validateCprAgainstServer,
											loanType: smartCheckSession!.clientInputs?.type,
											setConsumerInputsStepIndex,
											moduleIds:
												smartCheckSession!.smartCheckConfiguration
													.moduleConfiguration.moduleIds,

											questionnaireStatus: (
												smartCheckSession!.questionnaireStatus ?? []
											).map((step) => ({
												answered: step.answered,
												moduleDefinitionType: step.moduleDefinitionType,
												shouldSkip: step.shouldSkip,
											})),
											clientInputCivilStatus:
												smartCheckSession!.clientInputs?.civilStatus ??
												undefined,
											regionId: smartCheckSession!.regionId,
											idDocumentNationalityExemptions:
												smartCheckSession!.smartCheckConfiguration
													.idDocumentNationalityExemptions,
										}}
										moduleSettings={{ handleOwnLoadingStates: true }}
									>
										<LazyQuestionnaire />
									</QuestionnaireModuleProvider>
								)}
							></SentryRoute>
							<SentryRoute
								path={config.routeCodes.debtOverview}
								exact
								render={() => (
									<DebtOverviewModuleProvider
										moduleType={ModuleDefinition.DebtOverview}
										startCall={budgetApiClient.getDebtOverviewEstimates}
										finishCall={budgetApiClient.finishDebtOverviewModule}
										config={{
											regionId: smartCheckSession!.regionId,
											clientName:
												smartCheckSession!.smartCheckConfiguration
													.legalBrandName ?? smartCheckSession?.client.name,
										}}
									>
										<LazyDebtOverview />
									</DebtOverviewModuleProvider>
								)}
							></SentryRoute>
							<SentryRoute
								path={config.routeCodes.assetOverview}
								exact
								render={() => (
									<AssetOverviewModuleProvider
										moduleType={ModuleDefinition.AssetOverview}
										startCall={startNoop}
										finishCall={noop}
										config={{
											flowHasBankModule:
												smartCheckSession!.smartCheckConfiguration.moduleConfiguration.moduleIds.includes(
													ModuleDefinition.Bank,
												),
											shouldInputCooperativeHomeValue:
												smartCheckSession!.smartCheckConfiguration
													.shouldInputCooperativeHomeValue,
										}}
									>
										<LazyAssetOverview />
									</AssetOverviewModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.assetExpenses}
								render={() => (
									<AssetExpensesModuleProvider
										moduleType={ModuleDefinition.AssetExpenses}
										startCall={startNoop}
										finishCall={noop}
										config={{
											applicantSessionId: smartCheckSession!.applicantSessionId,
											caseId: smartCheckSession!.case.id,
											clientInputsType: smartCheckSession!.clientInputs?.type,
											shouldHideAssetBudgets:
												smartCheckSession!.smartCheckConfiguration
													.shouldHideAssetBudgets,
											shouldShowIndicationOfResult:
												smartCheckSession!.smartCheckConfiguration
													.shouldShowIndicationOfResult,
										}}
									>
										<LazyAssetExpenses />
									</AssetExpensesModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.assetReplacement}
								render={() => (
									<AssetReplacementModuleProvider
										moduleType={ModuleDefinition.AssetReplacement}
										startCall={startNoop}
										finishCall={noop}
										config={{
											clientInputsType: smartCheckSession!.clientInputs?.type,
										}}
									>
										<LazyAssetReplacement />
									</AssetReplacementModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.savingsCapacity}
								exact
								render={() => (
									<SavingsCapacityModuleProvider
										moduleType={ModuleDefinition.SavingsCapacity}
										startCall={startNoop}
										finishCall={budgetApiClient.finishSavingsCapacity}
										config={{
											getSessionTransactionsByCategories:
												budgetApiClient.getSessionTransactionsByCategories,
										}}
									>
										<LazySavingsCapacity />
									</SavingsCapacityModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.customQuestionnaire}
								render={() => (
									<CustomQuestionnaireModuleProvider
										moduleType={ModuleDefinition.CustomQuestionnaire}
										startCall={budgetApiClient.getCustomQuestionnaireQuestions}
										finishCall={budgetApiClient.finishCustomQuestionnaireModule}
										config={{}}
									>
										<LazyCustomQuestionnaire />
									</CustomQuestionnaireModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.miniBudget}
								render={() => (
									<BudgetModuleProvider
										moduleType={ModuleDefinition.Budget}
										startCall={budgetApiClient.getBudgetEstimates}
										finishCall={budgetApiClient.finishBudgetModule}
										config={{
											budgetType:
												smartCheckSession!.smartCheckConfiguration
													.moduleConfiguration.budgetType,
											shouldUseBudgetHelper:
												smartCheckSession!.smartCheckConfiguration
													.shouldUseBudgetHelper,
											shouldShowConsumerNote:
												smartCheckSession!.smartCheckConfiguration
													.shouldShowConsumerNote,
											shouldAllowFileUpload:
												smartCheckSession!.smartCheckConfiguration
													.shouldAllowFileUpload,
											shouldHideDiscretionaryIncome:
												smartCheckSession!.smartCheckConfiguration
													.shouldHideDiscretionaryIncome,
											partOfMultiFlow:
												smartCheckSession?.partOfMultiFlow ?? false,
											regionId: smartCheckSession!.regionId,
											clientName:
												smartCheckSession!.smartCheckConfiguration
													.legalBrandName ?? smartCheckSession?.client.name,
										}}
									>
										<LazyBudget />
									</BudgetModuleProvider>
								)}
							></SentryRoute>
							<SentryRoute
								path={config.routeCodes.budgetConsumerEmail}
								exact
								render={() => (
									<BudgetConsumerEmailModuleProvider
										moduleType={ModuleDefinition.BudgetConsumerEmail}
										startCall={startNoop}
										finishCall={noop}
										config={{
											isFinalFlow: smartCheckSession!.isFinalFlow,
											sendBudgetHelperPdf: budgetApiClient.sendBudgetHelperPdf,
										}}
									>
										<LazyBudgetConsumerEmail />
									</BudgetConsumerEmailModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.budgetCreator}
								exact
								render={() => (
									<BudgetCreatorModuleProvider
										moduleType={ModuleDefinition.FlowCompletion}
										startCall={startNoop}
										finishCall={noop}
										config={{ finishSession: budgetApiClient.finishSession }}
									>
										<LazyBudgetCreator />
									</BudgetCreatorModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.documentUpload}
								exact
								render={() => (
									<DocumentUploadModuleProvider
										moduleType={ModuleDefinition.DocumentUpload}
										startCall={async () => {
											const params = new URLSearchParams(location.search)
											const documentTypesParam = params.get('documentTypes')
											const documentTypes: DocumentationRequestDocumentType[] =
												documentTypesParam?.split(',') ?? []

											return {
												caseId: smartCheckSession!.case.id,
												clientName:
													smartCheckSession!.smartCheckConfiguration
														.legalBrandName ??
													smartCheckSession!.client.name ??
													'',
												documentTypes,
											}
										}}
										finishCall={noop}
										config={{
											updateCaseProcessingStatus:
												budgetApiClient.postCaseProcessingStatus,
											uploadFiles: budgetApiClient.uploadFiles,
										}}
									>
										<LazyDocumentUpload />
									</DocumentUploadModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.flowCompleted}
								exact
								render={() => (
									<FlowCompletedModuleProvider
										moduleType={ModuleDefinition.Finished}
										startCall={async () => ({
											hasIncomeVerification:
												smartCheckConfiguration!.moduleConfiguration.moduleIds.includes(
													ModuleDefinition.IncomeVerification,
												),
											isFinished: smartCheckSession!.finishedOn != null,
											shouldAllowOutputPdfDownload:
												smartCheckConfiguration!.shouldAllowOutputPdfDownload,
											shouldAllowRedirectToNextSession:
												smartCheckConfiguration!
													.shouldAllowRedirectToNextSession,
											shouldAutoCloseTabOnFlowCompletion:
												smartCheckSession!.shouldAutoCloseTabOnFlowCompletion,
											shouldShowIndicationOfResult:
												smartCheckConfiguration!.shouldShowIndicationOfResult,
											shouldRedirectWithoutDelay:
												smartCheckConfiguration!.redirectOptions
													.shouldRedirectWithoutDelay,
											shouldAutoCloseTabWithoutDelay:
												smartCheckConfiguration!.closeTabOptions
													.shouldAutoCloseTabWithoutDelay,
											redirectUrl: redirectService.getRedirectUrl(),
											redirectUrlNoQueryParams:
												smartCheckSession!.smartCheckConfiguration
													.redirectOptions.url,
											clientInputs: smartCheckSession?.clientInputs,
											mainApplicantId: smartCheckSession?.case.mainApplicantId,
										})}
										finishCall={async () => Promise.resolve()}
										config={{
											clientName:
												smartCheckSession!.smartCheckConfiguration
													.legalBrandName ??
												smartCheckSession?.client.name ??
												'',
											isFinalFlow: smartCheckSession!.isFinalFlow,
											partOfMultiFlow: smartCheckSession!.partOfMultiFlow,
											regionId: smartCheckSession!.regionId,
										}}
									>
										<LazyFlowCompleted />
									</FlowCompletedModuleProvider>
								)}
							/>
							<SentryRoute
								path={config.routeCodes.incompleteParringApplicantWarning}
								exact
								render={() => <ParringIncompleteScreen />}
							/>
							<SentryRoute path={config.routeCodes.flowDeleted} exact>
								<FlowDeletedModuleProvider
									moduleType={ModuleDefinition.Deleted}
									startCall={startNoop}
									finishCall={noop}
									config={undefined}
								>
									<LazyFlowDeleted />
								</FlowDeletedModuleProvider>
							</SentryRoute>
							<SentryRoute
								path={config.routeCodes.samsungBrowserWarning}
								render={() => (
									<SamsungBrowserWarningModuleProvider
										moduleType={ModuleDefinition.SamsungBrowserWarning}
										startCall={startNoop}
										finishCall={noop}
										config={undefined}
									>
										<LazySamsungBrowserWarning />
									</SamsungBrowserWarningModuleProvider>
								)}
							/>
						</Switch>
					</AppContentWithAnimation>
				</PageWrapper>
			</PrimaryNamespaceTranslations>
		</RegionProvider>
	)
}

function mapStateToProps(state: RootState) {
	return {
		authenticateState: state.authentication,
		smartCheckSession: state.authentication.smartCheckAuthenticationDto,
		smartCheckConfiguration:
			state.authentication.smartCheckAuthenticationDto?.smartCheckConfiguration,
		applicantId: state.authentication.applicantId,
		flowEntryStatus: state.flowEntryStatemachine.status,
	}
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		goToFlow: (applicantId: string) => dispatch(reduxGoToFlow(applicantId)),
		goToWhitelabelSubdomainLanding: () =>
			dispatch(goToWhitelabelSubdomainLanding()),
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(Routes)